import {
  getV1CompaniesCompanyIdInvoicesInvoiceIdInvoiceLineItems,
  patchV1CompaniesCompanyIdInvoicesInvoiceIdInvoiceLineItems,
} from '@treadinc/horizon-api-spec';
import { useState } from 'react';

import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { fetchAllApiPages } from '~utils/fetchAllPages';

const useInvoiceLineItems = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { invoiceLineItemsStore } = useStores();

  const getAllInvoiceLineItems = async (
    companyId: string,
    invoiceId: string,
    materialRateItemized: boolean,
    signal?: AbortSignal,
  ) => {
    setIsLoading(true);

    try {
      const lineItems = await fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdInvoicesInvoiceIdInvoiceLineItems({
            path: { 'company-id': companyId, 'invoice-id': invoiceId },
            query: { material_rate_itemized: materialRateItemized, ...query },
            signal: pageSignal,
          }),
        InvoiceLineItem.parse,
        { signal },
      );
      invoiceLineItemsStore.setInvoiceLineItemsByInvoiceId(invoiceId, lineItems);

      return lineItems;
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const batchUpdateInvoiceLineItems = async (
    companyId: string,
    invoiceId: string,
    lineItems: Array<Partial<InvoiceLineItem>>,
  ) => {
    try {
      const payload = {
        invoice_line_items: lineItems.map((lineItem) => {
          return InvoiceLineItem.deparseUpdate(lineItem);
        }),
      };

      const response = await patchV1CompaniesCompanyIdInvoicesInvoiceIdInvoiceLineItems({
        path: { 'company-id': companyId, 'invoice-id': invoiceId },
        body: payload,
      });

      const updatedlineItems = response.data.data.map((lineItem) => {
        return InvoiceLineItem.parse(lineItem);
      });
      // Bypass the edit lock — PATCH responses are authoritative saves and
      // must commit even while the invoice is still in edit mode.
      invoiceLineItemsStore.setInvoiceLineItemsByInvoiceId(invoiceId, updatedlineItems, {
        bypassEditLock: true,
      });

      return updatedlineItems;
    } catch (error) {
      connection.handleRequestError(error);
    }
  };

  return {
    batchUpdateInvoiceLineItems,
    getAllInvoiceLineItems,
    isLoading,
  };
};

export default useInvoiceLineItems;
